import { Breadcrumb, Layout, Steps } from "antd"
import { LayoutLoading, LayoutPageContent, LayoutPageHeader } from "components"
import { useGetAppConfig } from "contexts/configuration/hooks/use-get-app-config"
import { useGetNewQuoteUrl } from "contexts/configuration/hooks/use-get-new-quote-url"
import { useApiInfo } from "contexts/configuration/hooks/use-api-info"
import { useToken } from "contexts/authorization/hooks/use-token"
import React, { useEffect, useReducer, useRef } from "react"
import { Link, useHistory } from "react-router-dom"
import styled from "styled-components"
import SelectClientStep from "./SelectClientStep"
import ChooseProduct, { IActiveProduct } from "components/ChooseProduct"
import { IPolicyholder } from "platform-client/types"
import { FileTextOutlined } from "@ant-design/icons"
import { NewQuote } from "containers/quotes/views/NewQuote"
import usePlatform from "contexts/platform/use-platform"
import { useQuery } from "react-query"

const PaddedWrapper = styled.div`
  height: 100%;
  margin: 0 0 15px 0;
  padding: 20px;
  background: #ffffff;
`

enum CreateQuoteActions {
  UPDATE_PRODUCT,
  UPDATE_POLICYHOLDER,
  NEXT_STEP,
  PREV_STEP,
  SELECT_STEP,
  RESET
}

const initialState: ICreateQuoteForClientState = {
  step: 0,
  product: undefined,
  policyholder: undefined
}

const reducer = (
  state: ICreateQuoteForClientState,
  action: CreateQuoteForClientAction
): ICreateQuoteForClientState => {
  switch (action.type) {
    case CreateQuoteActions.SELECT_STEP:
      return { ...state, step: action.payload }
    case CreateQuoteActions.NEXT_STEP:
      return { ...state, step: state.step + 1 }
    case CreateQuoteActions.PREV_STEP:
      return {
        ...state,
        step: state.step > 0 ? state.step - 1 : 0
      }
    case CreateQuoteActions.UPDATE_PRODUCT:
      return { ...state, product: action.payload }
    case CreateQuoteActions.UPDATE_POLICYHOLDER:
      return { ...state, policyholder: action.payload }
    case CreateQuoteActions.RESET:
      return { ...initialState }
    default:
      return state
  }
}

interface ICreateQuoteForClientProps {
  policyholderId?: string
}

const CreateQuoteForClient: React.FC<ICreateQuoteForClientProps> = ({
  policyholderId
}) => {
  const isPreSelectedClient = Boolean(policyholderId)

  const [{ step, product, policyholder }, dispatch] = useReducer(
    reducer,
    initialState
  )

  const history = useHistory()
  const platform = usePlatform()
  const { apiInfo } = useApiInfo()
  const { userToken } = useToken()
  const { applicationConfig, isLoading: isAppConfigLoading } = useGetAppConfig()

  const { data: fetchedPolicyholder } = useQuery(
    ["getPolicyholder", policyholderId],
    () => {
      if (!policyholderId)
        return Promise.reject(new Error("No policyholder ID"))
      return platform.policyholders.getPolicyholder(policyholderId)
    },
    { enabled: isPreSelectedClient && !!policyholderId }
  )

  useEffect(() => {
    if (isPreSelectedClient && fetchedPolicyholder && !policyholder) {
      dispatch({
        type: CreateQuoteActions.UPDATE_POLICYHOLDER,
        payload: fetchedPolicyholder
      })
    }
  }, [isPreSelectedClient, fetchedPolicyholder, policyholder])

  const prevPolicyholderId = useRef(policyholderId)
  useEffect(() => {
    if (
      prevPolicyholderId.current !== undefined &&
      policyholderId === undefined
    ) {
      dispatch({ type: CreateQuoteActions.RESET })
    }
    prevPolicyholderId.current = policyholderId
  }, [policyholderId])

  const linesOfBusiness = applicationConfig?.activeLinesOfBusiness ?? []

  const activeProducts = linesOfBusiness
    .filter((item) => item.isActive)
    .map((item) =>
      item.coverTypes.map((coverType) => ({
        ...coverType,
        ...item,
        coverType: item.coverTypes[0]?.name
      }))
    )
    .flat()
    .sort((a, b) => (a?.dashboardOrder ?? 0) - (b?.dashboardOrder ?? 0))

  const nextStep = () => dispatch({ type: CreateQuoteActions.NEXT_STEP })
  const skipToStep = (stepIndex: number) =>
    dispatch({ type: CreateQuoteActions.SELECT_STEP, payload: stepIndex })

  const effectivePolicyholder = policyholder ?? fetchedPolicyholder

  const { data: company, isLoading: isCompanyLoading } = useQuery(
    ["getCompany", effectivePolicyholder?.companyID],
    () => {
      const cid = effectivePolicyholder?.companyID
      if (!cid) return Promise.reject(new Error("No company ID"))
      return platform.company.getCompany(cid)
    },
    { enabled: !!effectivePolicyholder?.companyID }
  )

  const enabledProductReferenceIds = new Set(
    company?.enabledProducts?.map((p) => p.referenceID) ?? []
  )

  const activeProductsForCompany = !effectivePolicyholder?.companyID
    ? activeProducts
    : company === undefined
    ? []
    : activeProducts.filter((p) =>
        enabledProductReferenceIds.has(p.productReferenceId)
      )

  const isChooseProductLoading =
    isAppConfigLoading ||
    (!!effectivePolicyholder?.companyID && isCompanyLoading)

  const { finalUrl } = useGetNewQuoteUrl(
    "new",
    apiInfo,
    product?.productReferenceId,
    product?.coverType ?? "comprehensive",
    undefined,
    undefined,
    undefined,
    undefined,
    effectivePolicyholder?.id
  )

  const selectClientStep = {
    title: "Select client",
    component: (
      <SelectClientStep
        client={policyholder}
        onSaveClient={(data: IPolicyholder) => {
          dispatch({
            type: CreateQuoteActions.UPDATE_POLICYHOLDER,
            payload: data
          })
          nextStep()
        }}
      />
    ),
    enabled: true
  }

  const chooseProductStep = {
    title: "Choose product",
    component: (
      <ChooseProduct
        onChooseProduct={(p: IActiveProduct) => {
          dispatch({ type: CreateQuoteActions.UPDATE_PRODUCT, payload: p })
          nextStep()
        }}
        activeProducts={activeProductsForCompany}
        isLoading={isChooseProductLoading}
      />
    ),
    enabled: effectivePolicyholder !== undefined
  }

  const openQuoteStep = {
    title: "Create quote",
    component:
      finalUrl && userToken ? (
        <NewQuote
          url={finalUrl}
          token={userToken}
          quoteSection="new"
          history={history}
          hideHeader
        />
      ) : (
        <LayoutLoading />
      ),
    enabled: product !== undefined && effectivePolicyholder !== undefined
  }

  const subViews = isPreSelectedClient
    ? [chooseProductStep, openQuoteStep]
    : [selectClientStep, chooseProductStep, openQuoteStep]

  const isLoadingPreSelectedClient =
    isPreSelectedClient && !!policyholderId && !fetchedPolicyholder

  if (isLoadingPreSelectedClient) {
    return (
      <Layout>
        <LayoutPageHeader
          ghost={false}
          breadcrumb={
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/quotes">
                  <FileTextOutlined />
                  &nbsp; Quotes
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>New quote</Breadcrumb.Item>
            </Breadcrumb>
          }
          title="New quote"
        />
        <LayoutPageContent style={{ height: "100%" }} transparent={true}>
          <LayoutLoading />
        </LayoutPageContent>
      </Layout>
    )
  }

  return (
    <Layout>
      <LayoutPageHeader
        ghost={false}
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/quotes">
                <FileTextOutlined />
                &nbsp; Quotes
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>New quote</Breadcrumb.Item>
          </Breadcrumb>
        }
        title="New quote"
      />

      <LayoutPageContent style={{ height: "100%" }} transparent={true}>
        <PaddedWrapper>
          <Steps
            current={step}
            style={{ padding: "15px" }}
            onChange={skipToStep}
          >
            {subViews.map((item, index) => (
              <Steps.Step
                key={index}
                title={item.title}
                disabled={!item.enabled}
              />
            ))}
          </Steps>
        </PaddedWrapper>
        <PaddedWrapper>
          {subViews[step]?.component ?? "Page not found"}
        </PaddedWrapper>
      </LayoutPageContent>
    </Layout>
  )
}

interface ICreateQuoteForClientState {
  step: number
  product?: IActiveProduct
  policyholder?: IPolicyholder
}

type CreateQuoteForClientAction =
  | { type: CreateQuoteActions.UPDATE_PRODUCT; payload: IActiveProduct }
  | { type: CreateQuoteActions.UPDATE_POLICYHOLDER; payload: IPolicyholder }
  | { type: CreateQuoteActions.NEXT_STEP }
  | { type: CreateQuoteActions.PREV_STEP }
  | { type: CreateQuoteActions.SELECT_STEP; payload: number }
  | { type: CreateQuoteActions.RESET }

export default CreateQuoteForClient
